import React, { useEffect, useRef, useState } from "react";
import "./advanceFilter.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { TiArrowSortedDown } from "react-icons/ti"; // Import the icon
import { set } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { setSeverityFilter } from "../../../redux/slices/operationHomeSlice";

const SEARCH_DEBOUNCE_MS = 500;
// Below this length, a search is almost always a no-match "in progress"
// keystroke (e.g. typing "r" while aiming for "rohit") — skip the network
// call/toast for it and wait for more input instead of firing + no-results
// toast on every single character.
const MIN_AUTO_SEARCH_LENGTH = 2;

const SearchFilter = ({ searchTerm, onSearchChange, onSearch }) => {
  const [client, setClient] = useState("");
  // "Searching..." is shown only for a severity-filtered search started via
  // the Search button — not for the global loading flag (initial load,
  // debounced typing, dropdown changes).
  const [isButtonSearching, setIsButtonSearching] = useState(false);
  const dispatch = useDispatch();

  const severity = useSelector((state) => state.operationHome.activeSeverity);
  const loading = useSelector((state) => state.operationHome.loading);

  const lastSearchedRef = useRef("");
  const debounceRef = useRef(null);

  const handleInputChange = (e) => {
    onSearchChange(e);
  };

  // Debounce as-you-type search: fires ~400ms after the user stops typing,
  // and only re-searches when the trimmed value actually changed.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const trimmed = (searchTerm ?? "").trim();
      if (trimmed === lastSearchedRef.current) return;
      // Let a clear (empty) search through immediately to reset the table,
      // but hold off auto-searching on a too-short in-progress term.
      if (trimmed.length > 0 && trimmed.length < MIN_AUTO_SEARCH_LENGTH) return;
      lastSearchedRef.current = trimmed;
      onSearch({ client, severity });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const triggerImmediateSearch = () => {
    const trimmed = (searchTerm ?? "").trim();
    if (loading) return; // avoid duplicate in-flight requests
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastSearchedRef.current = trimmed;
    onSearch({ client, severity });
  };

  useEffect(() => {
    if (!loading) setIsButtonSearching(false);
  }, [loading]);

  const handleSearchClick = () => {
    if (loading) return;
    if (severity) setIsButtonSearching(true);
    triggerImmediateSearch();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      triggerImmediateSearch();
    }
  };

  const handleClientChange = (e) => {
    const nextClient = e.target.value;
    console.log("[SearchFilter] client dropdown changed → payload:", nextClient);
    setClient(nextClient);
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    lastSearchedRef.current = (searchTerm ?? "").trim();
    onSearch({ client: nextClient, severity });
  };

  // Selecting a severity only stores the filter — the API call fires when
  // the user clicks the Search button.
  const handleSeverityChange = (e) => {
    const nextSeverity = e.target.value;
    console.log("[SearchFilter] dispatch setSeverityFilter → payload:", nextSeverity);
    dispatch(setSeverityFilter(nextSeverity));
  };

  return (
    <div className="search-bar">
      <div className="search-icon2">
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </div>
      <input
        type="text"
        placeholder="MH ID, Vonage ID, Agent Name, Call Status, Member Name"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown} // Add this to handle Enter key
      />
      <div className="dropdown-container">
        <select
          className="client-dropdown"
          value={client}
          onChange={handleClientChange}
        >
          <option value="">Employer Group: All</option>
          <option value="MonteNido">Monte Nido</option>
          {/* <option value="Healthrageous">Healthrageous</option> */}
        </select>
        <TiArrowSortedDown className="dropdown-icon" />
      </div>
      <div className="dropdown-container">
        <select
          className="severity-dropdown"
          value={severity}
          onChange={handleSeverityChange}
        >
          <option value="">Severity Level</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="N/A">N/A</option>
        </select>
        <TiArrowSortedDown className="dropdown-icon" />
      </div>
      <button
        className="search-button"
        onClick={handleSearchClick}
        disabled={loading}
        aria-busy={isButtonSearching}
      >
        {isButtonSearching ? "Searching..." : "Search"}
      </button>
    </div>
  );
};

export default SearchFilter;
